import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { accessRequestsApi, resourcesApi } from '../services/api';
import { AccessRequest, Resource } from '../types';
import { Plus, Key, Inbox, ShieldCheck, KeyRound, ClipboardList, Cloud, Server, Eye, ClipboardCheck, Ban, AlertTriangle, SearchX, Users } from 'lucide-react';
import { format } from 'date-fns';
import AccessRequestForm from '../components/AccessRequestForm';
import DenyAccessRequestModal from '../components/modals/DenyAccessRequestModal';
import RequestDetailDrawer from '../components/RequestDetailDrawer';
import StatusTimeline from '../components/StatusTimeline';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from '@tanstack/react-table';
import { UserInfo, useUser } from '../contexts/UserContext';
import { usePermissions } from '../hooks/usePermissions';
import { Button, Card, Chip, EmptyState, IconButton, OutcomeBadge, PageHeader, PaginationBar, SearchField, Select, StatusBadge, Tabs, TanstackTable, formatAbsolute, useTimeZonePreference } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { IconTile, ResourceTypeTile, resourceIcon } from '@/lib/resourceIcons';
import { errorMessage } from '@/lib/http';
import { copyWithToast } from '@/lib/copy';
import { toast } from '@/lib/toast';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, pageOffset, readPage, readRows } from '@/lib/urlPaging';
import { healthRank, timeValue } from '@/lib/sortValues';
import { describeScope, expiryIfApprovedNow, formatDuration, isOwnRequest, k8sScopeOf, requesterNameOf } from '@/features/access-requests/requestModel';

type AccessRequestsTab = 'requests' | 'approvals' | 'resources';

/** What a request is for, as the table names it. */
const requestTarget = (r: AccessRequest): string =>
  r.resource_name || r.resource_id || (r.request_type === 'role' ? `role ${r.role_id ?? ''}`.trim() : r.resource_type);

const columnHelper = createColumnHelper<any>();

// A missing or unparseable timestamp renders as a dash instead of throwing.
const formatDate = (value: string | null | undefined, pattern: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};
const resourceColumnHelper = createColumnHelper<Resource>();

/* ━━━━━━━━━━━━ Access Requests Table ━━━━━━━━━━━━━━━━━ */
interface AccessRequestsTableProps {
  requests: any[];
  isLoading: boolean;
  handleRevoke: (request: any) => void;
  isAdmin: boolean;
  userInfo: UserInfo | null;
  onViewDetails: (request: any) => void;
  searchTerm: string;
  'aria-label': string;
}

const AccessRequestsTable: React.FC<AccessRequestsTableProps> = ({
  requests,
  isLoading,
  handleRevoke,
  isAdmin,
  userInfo,
  onViewDetails,
  searchTerm,
  'aria-label': ariaLabel,
}) => {
  const columns = useMemo<ColumnDef<any, any>[]>(
    () => [
      columnHelper.display({
        id: 'user',
        header: 'User',
        meta: { sortable: true, sortValue: (r: AccessRequest) => (r.requested_by || '').toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)] font-semibold text-label uppercase flex-shrink-0">
              {row.original.requested_by?.charAt(0) || '?'}
            </div>
            <div>
              <div className="text-dense font-medium text-[var(--d2d-text-primary)]">
                {row.original.requested_by}
              </div>
              <div className="text-micro text-[var(--d2d-text-muted)]">
                {row.original.id.slice(0, 8)}…
              </div>
            </div>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'resource',
        header: 'Resource / Role',
        meta: { sortable: true, sortValue: (r: AccessRequest) => requestTarget(r).toLowerCase() },
        cell: ({ row }) => {
          const awsRoleArn = row.original.metadata?.aws_role_arn;
          const awsRoleName = awsRoleArn ? awsRoleArn.split('/').pop() : null;
          const awsDescription = row.original.metadata?.aws_access_description;
          const azureRoleName = row.original.metadata?.azure_role_name;
          const azureDescription = row.original.metadata?.azure_access_description;
          const hetznerPermission = row.original.metadata?.hetzner_permission_name;
          const hetznerDescription = row.original.metadata?.hetzner_access_description;
          return (
          <div className="flex items-center gap-2">
            <IconTile icon={row.original.request_type === 'role' ? KeyRound : resourceIcon(row.original.resource_type)} size={28} />
            <div>
              <div className="text-dense font-medium text-[var(--d2d-text-primary)]">
                {row.original.resource_name || row.original.resource_id || row.original.resource_type}
              </div>
              <div className="text-micro text-[var(--d2d-text-muted)] capitalize">
                {row.original.request_type === 'role' ? 'Role Access' : row.original.resource_type}
              </div>
              {awsDescription && (
                <span className="flex items-start gap-1 mt-1 px-2 py-0.5 rounded-md text-micro font-medium bg-[var(--zta-tint-warning)] text-[var(--d2d-warning)] max-w-[16rem] whitespace-normal break-words">
                  <ClipboardList className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden="true" />{awsDescription as string}
                </span>
              )}
              {azureDescription && (
                <span className="flex items-start gap-1 mt-1 px-2 py-0.5 rounded-md text-micro font-medium bg-[var(--zta-tint-info)] text-[var(--d2d-info)] max-w-[16rem] whitespace-normal break-words">
                  <ClipboardList className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden="true" />{azureDescription as string}
                </span>
              )}
              {awsRoleName && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-micro font-semibold bg-[var(--zta-tint-warning)] text-[var(--d2d-warning)]">
                  <Cloud className="h-3 w-3 flex-shrink-0" aria-hidden="true" />{awsRoleName}
                </span>
              )}
              {azureRoleName && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-micro font-semibold bg-[var(--zta-tint-info)] text-[var(--d2d-info)]">
                  <Cloud className="h-3 w-3 flex-shrink-0" aria-hidden="true" />{azureRoleName as string}
                </span>
              )}
              {hetznerDescription && (
                <span className="flex items-start gap-1 mt-1 px-2 py-0.5 rounded-md text-micro font-medium bg-[var(--zta-tint-danger)] text-[var(--danger-text)] max-w-[16rem] whitespace-normal break-words">
                  <ClipboardList className="h-3 w-3 mt-0.5 flex-shrink-0" aria-hidden="true" />{hetznerDescription as string}
                </span>
              )}
              {hetznerPermission && (
                <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-micro font-semibold bg-[var(--zta-tint-danger)] text-[var(--danger-text)]">
                  <Server className="h-3 w-3 flex-shrink-0" aria-hidden="true" />{hetznerPermission as string}
                </span>
              )}
            </div>
          </div>
          );
        },
      }),
      columnHelper.display({
        id: 'permissions',
        header: 'Permissions',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.permissions.map((permission: any, index: number) => (
              <Chip key={index} variant="neutral" className="capitalize">
                {permission}
              </Chip>
            ))}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        meta: { sortable: true, sortValue: (r: AccessRequest) => r.status },
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <OutcomeBadge value={row.original.status} decision className="capitalize">
              {row.original.status}
            </OutcomeBadge>
            <StatusTimeline request={row.original} compact />
          </div>
        ),
      }),
      columnHelper.accessor('duration_seconds', {
        header: 'Duration',
        meta: { sortable: true },
        cell: (info) => (
          <span className="text-dense text-[var(--d2d-text-secondary)] tabular-nums">
            {formatDuration(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('requested_at', {
        header: 'Requested',
        meta: { sortable: true, sortValue: (r: AccessRequest) => timeValue(r.requested_at) },
        cell: (info) => (
          <div>
            <div className="text-dense text-[var(--d2d-text-primary)]">
              {formatDate(info.getValue(), 'MMM d, yyyy')}
            </div>
            <div className="text-micro text-[var(--d2d-text-muted)]">
              {formatDate(info.getValue(), 'HH:mm')}
            </div>
          </div>
        ),
      }),
      ...(isAdmin ? [columnHelper.display({
        id: 'actions',
        header: () => <span className="text-right block">Actions</span>,
        cell: ({ row }) => {
          const request = row.original;
          // Approve and Deny live only in the request panel, which shows the
          // scope, justification and expiry being granted (design eval D6).
          return (
            <div className="flex items-center justify-end gap-1">
              {request.status === 'pending' && (
                <>
                  {isOwnRequest(request, userInfo) && (
                    <span className="text-micro text-[var(--d2d-text-muted)] italic mr-1">Your request</span>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    leadingIcon={<ClipboardCheck className="h-3.5 w-3.5" />}
                    onClick={(e) => { e.stopPropagation(); onViewDetails(request); }}
                    aria-label={`Review request from ${requesterNameOf(request)}`}
                  >
                    Review
                  </Button>
                </>
              )}
              {request.status === 'approved' && (
                <Button
                  variant="secondary"
                  size="sm"
                  leadingIcon={<Ban className="h-3.5 w-3.5" />}
                  onClick={(e) => { e.stopPropagation(); handleRevoke(request); }}
                >
                  Revoke
                </Button>
              )}
              {request.status !== 'pending' && (
                <IconButton
                  size="sm"
                  aria-label="View"
                  tooltip="View request details"
                  icon={<Eye className="h-4 w-4" />}
                  onClick={(e) => { e.stopPropagation(); onViewDetails(request); }}
                />
              )}
            </div>
          );
        },
      })] : [columnHelper.display({
        id: 'view',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <IconButton
              size="sm"
              aria-label="View"
              tooltip="View request details"
              icon={<Eye className="h-4 w-4" />}
              onClick={(e) => { e.stopPropagation(); onViewDetails(row.original); }}
            />
          </div>
        ),
      })]),
    ],
    [handleRevoke, onViewDetails, isAdmin, userInfo]
  );

  const table = useReactTable({
    data: requests,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <TanstackTable
          aria-label={ariaLabel}
          table={table}
          loading={isLoading}
          empty={
            searchTerm ? (
              <EmptyState
                icon={<SearchX />}
                title={`No requests on this page match "${searchTerm}"`}
                description="The search covers the requests on this page. Try another page or a different term."
              />
            ) : (
              <EmptyState
                icon={<Inbox />}
                title="No access requests found"
                description="Requests will appear here once submitted."
              />
            )
          }
        />
      </div>
    </Card>
  );
};

/* ━━━━━━━━━━━━ Resources Table ━━━━━━━━━━━━━━━━━━━━━━━ */
interface ResourcesTableProps {
  resources: Resource[];
  isLoading: boolean;
  getResourceTypeIcon: (type: string) => React.ReactNode;
  getEnvoyProxyUrl: (resource: Resource) => string | null;
  copyToClipboard: (text: string) => void;
  handleRequestAccess: (resource: Resource) => void;
  searchTerm: string;
}

const ResourcesTable: React.FC<ResourcesTableProps> = ({
  resources,
  isLoading,
  getResourceTypeIcon,
  getEnvoyProxyUrl,
  copyToClipboard,
  handleRequestAccess,
  searchTerm,
}) => {
  const columns = useMemo<ColumnDef<Resource, any>[]>(
    () => [
      resourceColumnHelper.display({
        id: 'resource',
        header: 'Resource',
        meta: { sortable: true, sortValue: (r: Resource) => (r.external_id || '').toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            {getResourceTypeIcon(row.original.type)}
            <div>
              <div className="text-dense font-medium text-[var(--d2d-text-primary)] font-mono tabular-nums">
                {row.original.external_id}
              </div>
              <div className="text-micro text-[var(--d2d-text-muted)]">
                {row.original.id.slice(0, 8)}…
              </div>
            </div>
          </div>
        ),
      }),
      resourceColumnHelper.accessor('type', {
        header: 'Type',
        meta: { sortable: true },
        cell: (info) => (
          <Chip variant="neutral" className="capitalize">{info.getValue()}</Chip>
        ),
      }),

      resourceColumnHelper.accessor('health_status', {
        header: 'Health',
        meta: { sortable: true, sortValue: (r: Resource) => healthRank(r.health_status) },
        cell: (info) => {
          const status = info.getValue();
          return status ? (
            <StatusBadge value={status} className="capitalize">{status}</StatusBadge>
          ) : null;
        },
      }),
      resourceColumnHelper.display({
        id: 'actions',
        header: () => <span className="text-right block">Actions</span>,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => handleRequestAccess(row.original)}
            >
              Request
            </Button>
          </div>
        ),
      }),
    ],
    [getResourceTypeIcon, getEnvoyProxyUrl, copyToClipboard, handleRequestAccess]
  );

  const table = useReactTable({
    data: resources,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <TanstackTable
          aria-label="Requestable resources"
          table={table}
          loading={isLoading}
          empty={
            searchTerm ? (
              <EmptyState
                icon={<SearchX />}
                title={`No resources on this page match "${searchTerm}"`}
                description="The search covers the resources on this page. Try another page or a different term."
              />
            ) : (
              <EmptyState
                icon={<ShieldCheck />}
                title="No resources found"
                description="Resources available for access will appear here."
              />
            )
          }
        />
      </div>
    </Card>
  );
};

/* ━━━━━━━━━━━━ Main Page ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export default function AccessRequests() {
  // The view, filters, search and paging live in the URL (design eval F24),
  // so a refresh, Back or a pasted link keeps them. Status and type are
  // server-side filters: changing either, or the tab, starts at page 1 (F7).
  //
  // The Request Access tab's "Select Role" filter is gone (F7): it was never
  // sent, and /resources/requestable takes only type, limit and offset, so the
  // backend scopes the list to the caller's roles itself.
  const [url, setUrl] = useUrlState({ tab: 'requests', status: '', type: '', q: '', page: '1', rows: '20' });
  const { userInfo } = useUser();
  const { isAdmin } = usePermissions();
  const activeTab: AccessRequestsTab =
    url.tab === 'resources' || (url.tab === 'approvals' && isAdmin()) ? (url.tab as AccessRequestsTab) : 'requests';
  // Pending approvals are pending requests, so that tab asks the server for
  // exactly those rather than filtering whatever page came back.
  const selectedStatus = activeTab === 'approvals' ? 'pending' : url.status;
  const selectedType = url.type;
  const searchTerm = url.q;
  const page = readPage(url.page);
  const itemsPerPage = readRows(url.rows);
  const setSearchTerm = (next: string) => setUrl({ q: next }, { replace: true });
  const [showForm, setShowForm] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [denyingRequest, setDenyingRequest] = useState<AccessRequest | null>(null);
  const [viewingRequest, setViewingRequest] = useState<AccessRequest | null>(null);

  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  const [zone] = useTimeZonePreference();

  const { data: requestsData, isLoading: requestsLoading, isFetching: requestsFetching, error: requestsError, refetch: refetchRequests } = useQuery(
    ['access-requests', selectedStatus, page, itemsPerPage],
    () => accessRequestsApi.list(selectedStatus || undefined, itemsPerPage, pageOffset(page, itemsPerPage)),
    { keepPreviousData: true, enabled: activeTab === 'requests' || activeTab === 'approvals' }
  );

  const { data: resourcesData, isLoading: resourcesLoading, isFetching: resourcesFetching, error: resourcesError, refetch: refetchResources } = useQuery(
    ['resources-for-access', selectedType, page, itemsPerPage],
    () => resourcesApi.listRequestable(selectedType || undefined, itemsPerPage, pageOffset(page, itemsPerPage)),
    { keepPreviousData: true, enabled: activeTab === 'resources' }
  );

  const approveMutation = useMutation(
    ({ id, awsRoleArn, azureRoleName, hetznerPermissionName }: { id: string; awsRoleArn?: string; azureRoleName?: string; hetznerPermissionName?: string }) => accessRequestsApi.approve(id, awsRoleArn, azureRoleName, hetznerPermissionName),
    {
      onSuccess: () => { queryClient.invalidateQueries('access-requests'); toast.success('Request approved'); },
      onError: (err) => { toast.error(errorMessage(err, 'Failed to approve request')); },
    }
  );

  const denyMutation = useMutation(
    ({ id, reason }: { id: string; reason: string }) => accessRequestsApi.deny(id, reason),
    {
      onSuccess: () => { queryClient.invalidateQueries('access-requests'); toast.success('Request denied'); },
      onError: (err) => { toast.error(errorMessage(err, 'Failed to deny request')); },
    }
  );

  const revokeMutation = useMutation(
    ({ id }: { id: string }) => accessRequestsApi.revoke(id),
    {
      onSuccess: () => { queryClient.invalidateQueries('access-requests'); toast.success('Access revoked'); },
      onError: (err) => { toast.error(errorMessage(err, 'Failed to revoke access')); },
    }
  );

  // Approving grants access, so it is confirmed, restating who gets what (and
  // with which cloud role), in which K8s scope, and until when. Approve exists
  // only in the request panel (design eval D6), so this is the one confirm.
  const handleApprove = async (request: AccessRequest, awsRoleArn?: string, azureRoleName?: string, hetznerPermissionName?: string) => {
    if (isOwnRequest(request, userInfo)) return; // the panel hides Approve; never send it
    const role = awsRoleArn || azureRoleName || hetznerPermissionName;
    const scope = describeScope(k8sScopeOf(request));
    const ends = formatAbsolute(expiryIfApprovedNow(request), zone, false);
    const ok = await confirm({
      title: 'Approve access?',
      message: `Grant ${requesterNameOf(request)} access to ${requestTarget(request)}${role ? ` as ${role}` : ''}${scope ? ` (${scope})` : ''} for ${formatDuration(request.duration_seconds)}, until ${ends}?`,
      confirmLabel: 'Approve',
      variant: 'warning',
    });
    if (!ok) return;
    try {
      await approveMutation.mutateAsync({ id: request.id, awsRoleArn, azureRoleName, hetznerPermissionName });
      setViewingRequest(null);
    } catch {
      /* reported by onError; the drawer stays open so the admin can retry */
    }
  };

  const handleDeny = (request: AccessRequest) => {
    if (isOwnRequest(request, userInfo)) return;
    setDenyingRequest(request);
    setViewingRequest(null);
  };

  const handleRevoke = async (request: AccessRequest) => {
    if (await confirm({
      title: 'Revoke access?',
      message: `Revoke ${request.requested_by}'s access to ${requestTarget(request)}?`,
      confirmLabel: 'Revoke',
      variant: 'danger',
    })) {
      try {
        await revokeMutation.mutateAsync({ id: request.id });
        setViewingRequest(null);
      } catch {
        /* reported by onError */
      }
    }
  };

  const confirmDeny = async (reason: string) => {
    if (denyingRequest) {
      try {
        await denyMutation.mutateAsync({ id: denyingRequest.id, reason });
        setDenyingRequest(null);
      } catch {
        /* reported by onError; the modal stays open with the reason intact */
      }
    }
  };

  const handleRequestAccess = (resource: Resource) => {
    setSelectedResource(resource);
    setShowForm(true);
  };

  // Filter requests for the different tabs
  const allRequests = requestsData?.data?.items || [];
  const myRequests = allRequests.filter((r: AccessRequest) =>
    r.requested_by.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.resource_type.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const pendingApprovals = allRequests.filter((r: AccessRequest) =>
    r.status === 'pending' && !isOwnRequest(r, userInfo)
  ).filter((r: AccessRequest) =>
    r.requested_by.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.resource_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredResources = resourcesData?.data.resources?.filter((resource: Resource) =>
    resource.external_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.type.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getResourceTypeIcon = (type: string) => <ResourceTypeTile type={type} />;

  const getEnvoyProxyUrl = (resource: Resource) => {
    if (resource.type === 'http_proxy') {
      const resourceId = resource.external_id;
      let proxyPath = '';
      if (resourceId.includes('web-app') || resourceId.includes('webapp')) proxyPath = '/services/web-app';
      else if (resourceId.includes('admin')) proxyPath = '/services/admin';
      else if (resourceId.includes('api')) proxyPath = '/api/';
      else proxyPath = `/services/${resourceId.replace(/[^a-zA-Z0-9-]/g, '-').toLowerCase()}`;
      return `${window.location.origin}${proxyPath}`;
    }
    return null;
  };

  const copyToClipboard = (text: string) => { void copyWithToast(text); };

  const isLoading = activeTab === 'resources' ? resourcesLoading : requestsLoading;
  const isFetching = activeTab === 'resources' ? resourcesFetching : requestsFetching;
  const error = activeTab === 'resources' ? resourcesError : requestsError;
  const refetch = activeTab === 'resources' ? refetchResources : refetchRequests;
  const totalItems = activeTab === 'resources'
    ? (resourcesData?.data?.total || 0)
    : (requestsData?.data?.total || 0);

  // A tab's count is the server's total for that view, shown on the open tab
  // (the only one loaded), not the length of whatever page came back.
  const TABS = [
    // /access-requests has no requester filter, and for an admin it returns
    // everyone's requests, so the tab says so rather than "My Requests" (F23).
    { key: 'requests' as const, label: isAdmin() ? 'All Requests' : 'My Requests', icon: isAdmin() ? Users : Key, count: activeTab === 'requests' ? totalItems : undefined },
    ...(isAdmin() ? [{ key: 'approvals' as const, label: 'Pending Approvals', icon: Inbox, count: activeTab === 'approvals' ? totalItems : undefined }] : []),
    { key: 'resources' as const, label: 'Request Access', icon: Plus, count: undefined },
  ];

  const handleRequestRole = () => {
    setSelectedResource(null);
    setShowForm(true);
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <PageHeader
        className="mb-6"
        icon={<KeyRound />}
        title="Access Requests"
        description="Manage JIT access to resources"
        primaryAction={
          <Button onClick={handleRequestRole} leadingIcon={<Plus className="h-4 w-4" />}>
            New Request
          </Button>
        }
        onRefresh={() => refetch()}
        refreshing={isFetching}
      />

      {/* Tabs */}
      <Tabs
        className="mb-6"
        value={activeTab}
        urlParam="tab"
        onChange={(v) => setUrl({ tab: v, page: null })}
        aria-label="Access request views"
      >
        <Tabs.List>
          {TABS.map((tab) => (
            <Tabs.Tab key={tab.key} value={tab.key}>
              <span className="flex items-center gap-2 whitespace-nowrap">
                <tab.icon className="h-4 w-4" aria-hidden="true" />
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <Chip variant="neutral">{tab.count}</Chip>
                )}
              </span>
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      {/* Filters */}
      <Card className="p-4 mb-6 overflow-visible">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            {/* /access-requests and /resources/requestable take no search
                param, so this filters the loaded page, and says so (F7). */}
            <SearchField
              label={activeTab === 'resources' ? 'Search resources on this page' : 'Search requests on this page'}
              placeholder={activeTab === 'resources' ? 'Search resources on this page…' : 'Search requests on this page…'}
              value={searchTerm}
              onChange={setSearchTerm}
              help={totalItems > itemsPerPage ? `Searches this page only (${itemsPerPage} of ${totalItems}).` : undefined}
            />
          </div>
          {activeTab === 'requests' && (
            <div className="sm:w-44">
              <Select
                className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
                label={<span className="sr-only">Status</span>}
                value={selectedStatus}
                onChange={(e) => setUrl({ status: e.target.value, page: null })}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </Select>
            </div>
          )}
          {activeTab === 'resources' && (
            <>
              <div className="sm:w-44">
                <Select
                  className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
                  label={<span className="sr-only">Resource type</span>}
                  value={selectedType}
                  onChange={(e) => setUrl({ type: e.target.value, page: null })}
                >
                  <option value="">All Types</option>
                  <option value="database">Database</option>
                  <option value="http_proxy">HTTP Proxy</option>
                  <option value="ssh">SSH Server</option>
                  <option value="k8s">Kubernetes</option>
                  <option value="desktop">Desktop</option>
                  <option value="aws_ec2,aws_s3,aws_route53,aws_account">AWS Resources</option>
                </Select>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Content */}
      {error ? (
        <Card className="overflow-hidden">
          <EmptyState
            icon={<AlertTriangle />}
            title={`Failed to load ${activeTab === 'resources' ? 'resources' : 'access requests'}`}
            description="Something went wrong while loading this list."
            action={<Button size="sm" variant="secondary" onClick={() => refetch()}>Retry</Button>}
          />
        </Card>
      ) : (<>
      {(activeTab === 'requests' || activeTab === 'approvals') && (
        <AccessRequestsTable
          requests={activeTab === 'approvals' ? pendingApprovals : myRequests}
          isLoading={isLoading}
          handleRevoke={handleRevoke}
          userInfo={userInfo}
          isAdmin={isAdmin()}
          onViewDetails={(req) => setViewingRequest(req)}
          searchTerm={searchTerm}
          aria-label={activeTab === 'approvals' ? 'Pending approvals' : isAdmin() ? 'All access requests' : 'My access requests'}
        />
      )}

      {activeTab === 'resources' && (
        <ResourcesTable
          resources={filteredResources}
          isLoading={isLoading}
          getResourceTypeIcon={getResourceTypeIcon}
          getEnvoyProxyUrl={getEnvoyProxyUrl}
          copyToClipboard={copyToClipboard}
          handleRequestAccess={handleRequestAccess}
          searchTerm={searchTerm}
        />
      )}
      </>)}

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="mt-4">
          {totalItems > 0 && <PaginationBar
            page={page}
            totalRows={totalItems}
            rowsPerPage={itemsPerPage}
            onPageChange={(p) => setUrl({ page: p })}
            onRowsPerPageChange={(n) => setUrl({ rows: n, page: null })}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
        </div>
      )}

      {/* ── Modals / Drawers ────────────────────────────── */}
      {showForm && (
        <AccessRequestForm
          resource={selectedResource}
          onClose={() => { setShowForm(false); setSelectedResource(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries('access-requests');
            setShowForm(false);
            setSelectedResource(null);
          }}
        />
      )}

      {denyingRequest && (
        <DenyAccessRequestModal
          requestedBy={denyingRequest.requested_by}
          onConfirm={confirmDeny}
          onClose={() => setDenyingRequest(null)}
          isLoading={denyMutation.isLoading}
        />
      )}

      {viewingRequest && (
        <RequestDetailDrawer
          request={viewingRequest}
          onClose={() => setViewingRequest(null)}
          onApprove={handleApprove}
          onDeny={handleDeny}
          onRevoke={handleRevoke}
          isActing={approveMutation.isLoading || revokeMutation.isLoading}
          isAdmin={isAdmin()}
          currentUser={userInfo}
        />
      )}
    </div>
  );
}
